// Dispatcher for commands originating from Dynamic Island / Lock Screen
// AppIntents. Routes each command through the same code paths the in-app
// UI uses, so behaviour stays consistent.
//
// Wiring:
// - main.tsx installs the listener and triggers a drain on cold-start.
// - app-lifecycle resume triggers a drain too (in case the user tapped an
//   intent while the app was suspended).

import {
  addLiveActivityCommandListener,
  drainLiveActivityCommands,
  type LiveActivityCommand,
} from "@/lib/native/live-activity";
import { setTrackingState } from "@/lib/native/tracking-state";
import {
  stopActiveWatch,
  startActiveWatch,
  getActiveWatchTripId,
} from "@/lib/native/active-watch";
import { recordManualGpsNow } from "@/lib/native/manual-gps";
import { setGpsPreset, type GpsPreset } from "@/lib/prefs";
import { reapplyActiveWatchAdaptive } from "@/lib/native/active-watch";
import { liveActivityController } from "@/lib/live-activity/controller";
import { kv } from "@/lib/native/kv";

const PAUSED_TRIP_KEY = "roava.liveActivity.pausedTripId";
const SEEN_SEQ_KEY = "roava.liveActivity.lastSeq";
const PENDING_END_CONFIRM_KEY = "roava.liveActivity.pendingEndConfirm";
export const CONFIRM_END_TRIP_EVENT = "roava:confirm-end-trip";

let installed = false;
const seenSeq = new Set<number>();
let lastSeqHigh = 0;

async function loadLastSeq() {
  try {
    const v = await kv.get(SEEN_SEQ_KEY);
    if (v) lastSeqHigh = Number(v) || 0;
  } catch { /* ignore */ }
}
async function persistLastSeq(seq: number) {
  if (seq <= lastSeqHigh) return;
  lastSeqHigh = seq;
  try { await kv.set(SEEN_SEQ_KEY, String(seq)); } catch { /* ignore */ }
}

const VALID_PRESETS = new Set<GpsPreset>(["battery_saver", "balanced", "high_detail"]);

async function dispatch(cmd: LiveActivityCommand) {
  if (!cmd || typeof cmd.seq !== "number") return;
  if (cmd.seq <= lastSeqHigh || seenSeq.has(cmd.seq)) return;
  seenSeq.add(cmd.seq);
  await persistLastSeq(cmd.seq);

  console.info(`[live-activity] command kind=${cmd.kind} seq=${cmd.seq}`);

  switch (cmd.kind) {
    case "confirmEndTrip": {
      // Destructive — never stop tracking from the Island/Lock Screen. Open
      // Roava (already happens via openAppWhenRun=true on the AppIntent) and
      // surface an in-app confirmation sheet. Tracking stays active until the
      // user explicitly confirms.
      try {
        const tripId = getActiveWatchTripId();
        console.info(`[live-activity] confirmEndTrip requested seq=${cmd.seq} tripId=${tripId ?? "none"}`);
        await kv.set(PENDING_END_CONFIRM_KEY, tripId ?? "1");
        try {
          window.dispatchEvent(
            new CustomEvent(CONFIRM_END_TRIP_EVENT, { detail: { tripId } }),
          );
        } catch { /* ignore — no window (SSR) */ }
      } catch (e) {
        console.warn("[live-activity] confirmEndTrip failed", e);
      }
      break;
    }
    case "endTrip": {
      // Legacy/back-compat path. New widget builds queue confirmEndTrip
      // instead, but if an older widget binary ever fires this we still
      // require the same end-tracking effects to run via the helper.
      await executeConfirmedEndTrip();
      break;
    }
    case "pause": {
      try {
        const tripId = getActiveWatchTripId();
        if (tripId) {
          try { await kv.set(PAUSED_TRIP_KEY, tripId); } catch { /* ignore */ }
          // keepLiveActivity: stop LocationBG but DO NOT dismiss the Apple
          // Live Activity. The controller below flips to "paused" and pushes
          // a normal update — the Island/Lock Screen stays alive and shows
          // Resume instead of disappearing.
          await stopActiveWatch({ keepLiveActivity: true, source: "command/pause" });
        }
        // Controller reconcile pushes update({isPaused:true}) via the bridge.
        // We intentionally do NOT call updateTrackingLiveActivity directly
        // here — the controller owns all bridge calls.
        liveActivityController.onTrackingPaused(true);
        console.info("[live-activity] pause applied — LA kept alive");
      } catch (e) {
        console.warn("[live-activity] pause failed", e);
      }
      break;
    }
    case "resume": {
      try {
        let tripId = getActiveWatchTripId();
        if (!tripId) {
          try { tripId = (await kv.get(PAUSED_TRIP_KEY)) || null; } catch { /* ignore */ }
        }
        if (tripId) {
          // startActiveWatch is idempotent for the same tripId — no duplicate
          // Live Activity is created. The controller already has tracking in
          // "paused" (non-"off"), so onTrackingPaused(false) only flips to
          // "active" and pushes update({isPaused:false}).
          await startActiveWatch(tripId);
          try { await kv.set(PAUSED_TRIP_KEY, ""); } catch { /* ignore */ }
        }
        liveActivityController.onTrackingPaused(false);
        console.info("[live-activity] resume applied");
      } catch (e) {
        console.warn("[live-activity] resume failed", e);
      }
      break;
    }
    case "manualFix": {
      // User-facing name is "Drop Pin" — see Live Activity widget label.
      // Internal command kind stays "manualFix" for back-compat with the
      // existing seq-dedupe persisted in kv.
      try {
        const tripId = getActiveWatchTripId();
        if (!tripId) {
          console.info("[live-activity] Drop Pin skipped reason=no-active-trip");
          break;
        }
        const res = await recordManualGpsNow(tripId);
        if (!res.ok) console.info(`[live-activity] Drop Pin failed reason=${res.reason}`);
        else console.info("[live-activity] Pin dropped");
      } catch (e) {
        console.warn("[live-activity] Drop Pin failed", e);
      }
      break;
    }
    case "setCadence": {
      const preset = cmd.args?.preset as GpsPreset | undefined;
      if (!preset || !VALID_PRESETS.has(preset)) {
        console.info(`[live-activity] setCadence ignored preset=${preset}`);
        break;
      }
      try {
        setGpsPreset(preset);
        // keepLiveActivity: watcher restarts internally with the new cadence
        // but the Apple Live Activity is preserved. refreshStats() then forces
        // an update so presetKey/presetLabel/intervalSeconds repaint.
        await reapplyActiveWatchAdaptive({ keepLiveActivity: true, source: `command/setCadence/${preset}` });
        liveActivityController.refreshStats();
        console.info(`[live-activity] setCadence applied preset=${preset} — LA kept alive`);
      } catch (e) {
        console.warn("[live-activity] setCadence failed", e);
      }
      break;
    }
    default:
      console.info(`[live-activity] unknown command kind=${cmd.kind}`);
  }
}

export async function initLiveActivityCommands(): Promise<void> {
  if (installed) return;
  installed = true;
  await loadLastSeq();
  // Drain anything queued while the app was suspended.
  await drainOnce();
  // If a confirmEndTrip was queued via the AppIntent during a cold-launch,
  // re-emit the event so a freshly-mounted ConfirmEndTripSheet can react.
  void rehydratePendingEndConfirm();
  // Subscribe to live taps while foregrounded.
  try {
    await addLiveActivityCommandListener((cmd) => { void dispatch(cmd); });
  } catch (e) {
    console.warn("[live-activity] listener install failed", e);
  }
}

export async function drainOnce(): Promise<void> {
  try {
    const cmds = await drainLiveActivityCommands();
    if (!cmds.length) return;
    cmds.sort((a, b) => a.seq - b.seq);
    for (const c of cmds) {
      // eslint-disable-next-line no-await-in-loop
      await dispatch(c);
    }
  } catch (e) {
    console.warn("[live-activity] drainOnce failed", e);
  }
}

/**
 * Re-fire the confirm-end-trip event if a previous AppIntent tap is still
 * pending (e.g. app was killed when End was tapped). The sheet listens for
 * this event AND also reads the kv key directly on mount, so this is a
 * belt-and-braces signal.
 */
export async function rehydratePendingEndConfirm(): Promise<void> {
  try {
    const stored = await kv.get(PENDING_END_CONFIRM_KEY);
    if (!stored) return;
    const tripId = stored === "1" ? null : stored;
    console.info(`[live-activity] confirmEndTrip rehydrated tripId=${tripId ?? "none"}`);
    try {
      window.dispatchEvent(
        new CustomEvent(CONFIRM_END_TRIP_EVENT, { detail: { tripId } }),
      );
    } catch { /* ignore */ }
  } catch { /* ignore */ }
}

/** True if an End tap is awaiting in-app confirmation. */
export async function hasPendingEndConfirm(): Promise<boolean> {
  try {
    const v = await kv.get(PENDING_END_CONFIRM_KEY);
    return !!v;
  } catch { return false; }
}

/** User confirmed End in the in-app sheet — actually stop tracking. */
export async function executeConfirmedEndTrip(): Promise<void> {
  try {
    await setTrackingState(false);
    await stopActiveWatch({ source: "confirmed-end-trip" });
    try { await kv.set(PAUSED_TRIP_KEY, ""); } catch { /* ignore */ }
    try { await kv.remove(PENDING_END_CONFIRM_KEY); } catch { /* ignore */ }
    console.info("[live-activity] endTrip confirmed");
  } catch (e) {
    console.warn("[live-activity] endTrip confirmed failed", e);
  }
}

/** User cancelled the End confirmation — keep tracking, clear the latch. */
export async function cancelPendingEndTrip(): Promise<void> {
  try { await kv.remove(PENDING_END_CONFIRM_KEY); } catch { /* ignore */ }
  console.info("[live-activity] endTrip cancelled");
}

